import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { useValueAssessmentTelemetry } from '@/hooks/useValueAssessmentTelemetry';

const VALUE_STATEMENTS = [
  { value: 'Achievement', statement: 'I feel most alive when I accomplish challenging goals' },
  { value: 'Creativity', statement: 'I need to express myself and create new things regularly' },
  { value: 'Connection', statement: 'Deep relationships are more important than achievements' },
  { value: 'Freedom', statement: 'Having autonomy over my work is non-negotiable for me' },
  { value: 'Growth', statement: 'I prioritize learning and self-improvement above comfort' },
  { value: 'Security', statement: 'Stability and predictability help me thrive' },
  { value: 'Impact', statement: 'Making a difference matters more than personal gain' },
  { value: 'Excellence', statement: 'I hold myself to very high standards in everything' },
  { value: 'Balance', statement: 'Work-life harmony is essential to my wellbeing' },
  { value: 'Leadership', statement: 'I naturally take charge and guide others' },
  { value: 'Authenticity', statement: 'Being true to myself is worth any cost' },
  { value: 'Recognition', statement: 'Being acknowledged for my work motivates me' },
  { value: 'Adventure', statement: 'I seek new experiences and embrace uncertainty' },
  { value: 'Service', statement: 'Helping others gives my life meaning' },
  { value: 'Knowledge', statement: 'Understanding deeply is more satisfying than doing quickly' },
];

interface QuickAssessmentProps {
  onComplete: (values: { name: string; description: string; importance: number }[]) => void;
  onCancel: () => void;
  initialDraftData?: Record<string, unknown>;
  onSaveDraft?: (data: Record<string, unknown>) => void;
  assessmentId?: string | null;
}

export const QuickAssessment = ({ onComplete, onCancel, initialDraftData, onSaveDraft, assessmentId }: QuickAssessmentProps) => {
  const [currentIndex, setCurrentIndex] = useState(() => {
    if (initialDraftData?.currentIndex != null) return initialDraftData.currentIndex as number;
    return 0;
  });
  const [ratings, setRatings] = useState<Record<string, number>>(() => {
    if (initialDraftData?.ratings) return initialDraftData.ratings as Record<string, number>;
    return {};
  });
  const [showResults, setShowResults] = useState(() => {
    if (initialDraftData?.showResults) return true;
    return false;
  });

  const {
    startAssessment,
    restoreSession,
    updateProgress,
    completeAssessment,
    abandonAssessment
  } = useValueAssessmentTelemetry();

  // Start or restore tracking when component mounts
  useEffect(() => {
    if (initialDraftData && assessmentId) {
      restoreSession(assessmentId, 'quick_assessment');
    } else {
      startAssessment('quick_assessment', 'dashboard');
    }
  }, []);

  const progress = ((currentIndex + 1) / VALUE_STATEMENTS.length) * 100;
  const current = VALUE_STATEMENTS[currentIndex];

  const handleRate = (rating: number) => {
    const newRatings = { ...ratings, [current.value]: rating };
    setRatings(newRatings);

    // Track progress
    updateProgress(currentIndex + 1, {
      quick_assessment_scores: newRatings,
    });

    if (currentIndex < VALUE_STATEMENTS.length - 1) {
      const nextIndex = currentIndex + 1;
      setCurrentIndex(nextIndex);
      onSaveDraft?.({ schema_version: 1, ratings: newRatings, currentIndex: nextIndex, showResults: false });
    } else {
      setShowResults(true);
      onSaveDraft?.({ schema_version: 1, ratings: newRatings, currentIndex, showResults: true });
    }
  };

  const getTopValues = () => {
    return Object.entries(ratings)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5)
      .map(([name], index) => ({
        name,
        description: VALUE_STATEMENTS.find(v => v.value === name)?.statement || '',
        importance: index + 1,
      }));
  };

  const handleComplete = () => {
    const topValues = getTopValues();
    completeAssessment(
      topValues.map(v => v.name),
      { quick_assessment_scores: ratings }
    );
    onComplete(topValues);
  };

  const handleCancel = () => {
    abandonAssessment('user_cancelled');
    onCancel();
  };

  if (showResults) {
    const topValues = getTopValues();
    return (
      <div className="space-y-4">
        <div className="text-center mb-4">
          <h3 className="font-semibold text-lg">Your Top 5 Values</h3>
          <p className="text-sm text-muted-foreground">
            Based on your ratings, here's what matters most to you
          </p>
        </div>

        <div className="space-y-3">
          {topValues.map((value, index) => (
            <div
              key={value.name}
              className="p-3 rounded-lg bg-primary/5 border border-primary/20 flex items-center gap-3"
            >
              <Badge variant="default" className="shrink-0">
                #{index + 1}
              </Badge>
              <div>
                <span className="font-medium">{value.name}</span>
                <p className="text-xs text-muted-foreground">{value.description}</p>
              </div>
            </div>
          ))}
        </div>

        <Button onClick={handleComplete} className="w-full gap-2">
          <CheckCircle2 className="w-4 h-4" />
          Save These Values
        </Button>
        <Button variant="ghost" onClick={handleCancel} className="w-full">
          Start Over
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Badge variant="outline">
          {currentIndex + 1} of {VALUE_STATEMENTS.length}
        </Badge>
        <Progress value={progress} className="w-24 h-2" />
      </div>

      <div className="text-center py-4">
        <p className="text-lg font-medium leading-relaxed">
          "{current.statement}"
        </p>
        <p className="text-sm text-muted-foreground mt-2">
          How strongly does this resonate with you?
        </p>
      </div>

      <div className="space-y-4">
        <div className="flex justify-between text-xs text-muted-foreground px-1">
          <span>Not at all</span>
          <span>Strongly</span>
        </div>
        <div className="grid grid-cols-5 gap-2">
          {[1, 2, 3, 4, 5].map((rating) => (
            <Button
              key={rating}
              variant={ratings[current.value] === rating ? 'default' : 'outline'}
              className="h-12 text-lg"
              onClick={() => handleRate(rating)}
            >
              {rating}
            </Button>
          ))}
        </div>
      </div>

      <Button variant="ghost" onClick={handleCancel} className="w-full">
        Cancel
      </Button>
    </div>
  );
};
